'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { BookOpen, Search, Plus, Loader2, CheckCircle, X, Volume2 } from 'lucide-react';

interface Language { id: string; name: string; dialects: string[]; }
interface DictEntry {
  id: string;
  word: string;
  dialect: string;
  meaning: string;
  english_equivalent: string;
  example_sentence: string;
  audio_url: string;
  alternative_spellings: string[];
  submission_status: string;
  created_at: string;
  mutauro_languages?: { name: string };
}

export default function MutauroDictionaryPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<DictEntry[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [query, setQuery] = useState('');
  const [filterLang, setFilterLang] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const supabase = createClient();

  const [form, setForm] = useState({
    language_id: '', word: '', dialect: '', meaning: '',
    english_equivalent: '', example_sentence: '', alternative_spellings: '',
  });

  useEffect(() => {
    supabase.from('mutauro_languages').select('id,name,dialects').order('name')
      .then(({ data }) => { if (data) setLanguages(data); });
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      let qb = supabase
        .from('mutauro_dictionary')
        .select('*, mutauro_languages(name)')
        .order('word');

      if (filterLang) qb = qb.eq('language_id', filterLang);
      if (query.trim()) qb = qb.or(`word.ilike.%${query}%,meaning.ilike.%${query}%,english_equivalent.ilike.%${query}%`);

      const { data } = await qb.limit(100);
      setEntries(data || []);
    } finally {
      setLoading(false);
    }
  }, [query, filterLang]);

  useEffect(() => {
    const t = setTimeout(loadEntries, 300);
    return () => clearTimeout(t);
  }, [loadEntries]);

  async function submitEntry(e: React.FormEvent) {
    e.preventDefault();
    if (!form.language_id || !form.word || !form.meaning) return;
    setFormLoading(true);
    try {
      const spellings = form.alternative_spellings.split(',').map(s => s.trim()).filter(Boolean);
      await supabase.from('mutauro_dictionary').insert({
        ...form,
        alternative_spellings: spellings,
        contributor_id: user?.id || null,
      });
      setFormSuccess(true);
      setForm({ language_id: '', word: '', dialect: '', meaning: '', english_equivalent: '', example_sentence: '', alternative_spellings: '' });
      setTimeout(() => { setFormSuccess(false); setShowForm(false); loadEntries(); }, 2000);
    } finally {
      setFormLoading(false);
    }
  }

  const inputCls = "w-full px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-200";
  const inputStyle = { borderColor: 'var(--border)', background: 'var(--input)', color: 'var(--foreground)' };
  const formLangDialects = languages.find(l => l.id === form.language_id)?.dialects || [];

  return (
    <AppLayout>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ background: '#CC000018', color: '#CC0000', fontFamily: 'var(--font-mono)' }}>Mutauro AI</span>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Digital Dictionary</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Emerging multilingual dictionary for Zimbabwe's indigenous languages.
            </p>
          </div>
          <button onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ background: '#CC0000', color: '#fff' }}>
            <Plus size={15} /> Add Word
          </button>
        </div>

        {/* Add Word Form */}
        {showForm && (
          <div className="mb-6 rounded-xl border p-5" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>Add Dictionary Entry</h2>
              <button onClick={() => setShowForm(false)}><X size={16} style={{ color: 'var(--muted-foreground)' }} /></button>
            </div>
            {formSuccess ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: '#1A6B3A' }}>
                <CheckCircle size={16} /> Entry submitted for review!
              </div>
            ) : (
              <form onSubmit={submitEntry} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Language *</label>
                    <select className={inputCls} style={inputStyle} value={form.language_id}
                      onChange={e => setForm(f => ({ ...f, language_id: e.target.value, dialect: '' }))}>
                      <option value="">Select…</option>
                      {languages.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Word *</label>
                    <input className={inputCls} style={inputStyle} placeholder="Indigenous word…"
                      value={form.word} onChange={e => setForm(f => ({ ...f, word: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Dialect</label>
                    <select className={inputCls} style={inputStyle} value={form.dialect}
                      onChange={e => setForm(f => ({ ...f, dialect: e.target.value }))}>
                      <option value="">Any dialect</option>
                      {formLangDialects.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Meaning *</label>
                    <textarea className={inputCls} style={inputStyle} rows={2} placeholder="Definition in the language or English…"
                      value={form.meaning} onChange={e => setForm(f => ({ ...f, meaning: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>English Equivalent</label>
                    <input className={inputCls} style={inputStyle} placeholder="Closest English word…"
                      value={form.english_equivalent} onChange={e => setForm(f => ({ ...f, english_equivalent: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Example Sentence</label>
                    <input className={inputCls} style={inputStyle} placeholder="Example usage…"
                      value={form.example_sentence} onChange={e => setForm(f => ({ ...f, example_sentence: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Alternative Spellings (comma-separated)</label>
                    <input className={inputCls} style={inputStyle} placeholder="e.g. moto, muto"
                      value={form.alternative_spellings} onChange={e => setForm(f => ({ ...f, alternative_spellings: e.target.value }))} />
                  </div>
                </div>
                <button type="submit" disabled={formLoading || !form.language_id || !form.word || !form.meaning}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
                  style={{ background: '#CC0000', color: '#fff' }}>
                  {formLoading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {formLoading ? 'Submitting…' : 'Submit Entry'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Search */}
        <div className="flex gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input className="w-full pl-9 px-3 py-2 rounded-lg border text-sm outline-none focus:ring-2 focus:ring-red-200"
              style={inputStyle} placeholder="Search words, meanings…"
              value={query} onChange={e => setQuery(e.target.value)} />
          </div>
          <select className="px-3 py-2 rounded-lg border text-sm outline-none" style={inputStyle}
            value={filterLang} onChange={e => setFilterLang(e.target.value)}>
            <option value="">All Languages</option>
            {languages.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>

        {/* Entries Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin" style={{ color: '#CC0000' }} />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 rounded-xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <BookOpen size={28} className="mx-auto mb-3" style={{ color: 'var(--muted-foreground)' }} />
            <div className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              {query || filterLang ? 'No entries match your search.' : 'No dictionary entries yet. Be the first to add a word!'}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {entries.map(entry => (
              <div key={entry.id} className="rounded-xl border p-4" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-lg font-bold" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-sans)' }}>{entry.word}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#CC000018', color: '#CC0000' }}>
                        {entry.mutauro_languages?.name}
                      </span>
                      {entry.dialect && (
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{entry.dialect}</span>
                      )}
                    </div>
                  </div>
                  {entry.audio_url && (
                    <button onClick={() => new Audio(entry.audio_url).play()}
                      className="p-1.5 rounded-lg border flex-shrink-0"
                      style={{ borderColor: 'var(--border)', background: 'var(--secondary)' }}>
                      <Volume2 size={14} style={{ color: '#CC0000' }} />
                    </button>
                  )}
                </div>
                <p className="text-sm mb-2" style={{ color: 'var(--foreground)' }}>{entry.meaning}</p>
                {entry.english_equivalent && (
                  <p className="text-xs mb-1" style={{ color: 'var(--muted-foreground)' }}>
                    <span className="font-medium">English:</span> {entry.english_equivalent}
                  </p>
                )}
                {entry.example_sentence && (
                  <p className="text-xs italic" style={{ color: 'var(--muted-foreground)' }}>"{entry.example_sentence}"</p>
                )}
                {entry.alternative_spellings?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.alternative_spellings.map(s => (
                      <span key={s} className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>{s}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
